import type { OnboardingModule } from "../../../api/types";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { EmptyState } from "../../../ui/EmptyState";
import { Pill } from "../../../ui/Pill";
import { moduleStatusWord } from "../shared/words";
import type { ModuleActions } from "./useModuleActions";

const GROUP_TITLES: Record<string, string> = {
  E: "Engineer path",
  S: "SME path",
  A: "Admin path",
};

/** Modules by the letter of their code: E for engineers, S for SMEs, A for the admin. */
export function groupModules(modules: readonly OnboardingModule[]): Array<{ letter: string; title: string; modules: OnboardingModule[] }> {
  const groups = new Map<string, OnboardingModule[]>();
  for (const module of modules) {
    const letter = module.module.charAt(0).toUpperCase();
    groups.set(letter, [...(groups.get(letter) ?? []), module]);
  }
  return Array.from(groups, ([letter, list]) => ({ letter, title: GROUP_TITLES[letter] ?? "Other modules", modules: list }));
}

interface ModuleRowProps {
  module: OnboardingModule;
  actions: ModuleActions;
  /** The agent can teach: Take, Resume and "skip by showing" need it. */
  agentAvailable: boolean;
}

function ModuleRow({ module, actions, agentAvailable }: ModuleRowProps) {
  const busy = actions.busyModule !== null;
  const mine = actions.busyModule === module.module;
  const name = `${module.module}: ${module.title}`;
  const finished = module.status === "done" || module.status === "known" || module.status === "skipped";
  return (
    <li className="ra-module" data-status={module.status} data-module={module.module}>
      <div className="ra-module__head">
        <strong>{module.module}</strong> <span>{module.title}</span> <Pill>{moduleStatusWord(module.status)}</Pill>
      </div>
      <div className="ra-module__actions row">
        {module.status === "not_started" && (
          <>
            <Button variant="primary" disabled={busy || !agentAvailable} busy={mine} onClick={() => void actions.take(module)} aria-label={`Take module ${name}`}>
              Take it
            </Button>
            {module.can_skip_by_showing && (
              <Button disabled={busy || !agentAvailable} onClick={() => void actions.skipByShowing(module)} aria-label={`Skip module ${name} by showing you know it`}>
                Skip by showing you know it
              </Button>
            )}
            <Button variant="quiet" disabled={busy} onClick={() => void actions.skip(module)} aria-label={`Skip module ${name}`}>
              Skip it
            </Button>
          </>
        )}
        {module.status === "started" && (
          <>
            <Button variant="primary" disabled={busy || !agentAvailable} busy={mine} onClick={() => void actions.resume(module)} aria-label={`Resume module ${name}`}>
              Resume
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => void actions.skip(module)} aria-label={`Skip module ${name}`}>
              Skip it
            </Button>
          </>
        )}
        {finished && (
          <Button disabled={busy || !agentAvailable} onClick={() => void actions.again(module)} aria-label={`Go through module ${name} again`}>
            Go through it again
          </Button>
        )}
      </div>
    </li>
  );
}

interface ModuleListProps {
  modules: readonly OnboardingModule[];
  actions: ModuleActions;
  agentAvailable: boolean;
}

/** The modules of the person's path, with take, skip, resume and "skip by showing" (ui-direction screen 1). Optional and never graded. */
export function ModuleList({ modules, actions, agentAvailable }: ModuleListProps) {
  if (modules.length === 0) {
    return (
      <EmptyState title="No module is offered for your role yet">
        When your role in a discipline is set, the short modules for it appear here.
      </EmptyState>
    );
  }
  return (
    <div className="stack">
      {!agentAvailable && (
        <p className="muted" role="note">
          The agent teaches the modules, so Take, Resume and "skip by showing" wait until it is available. You can still skip a module.
        </p>
      )}
      {actions.error && (
        <p role="alert" className="form-error">
          {actions.error}
        </p>
      )}
      {groupModules(modules).map((group) => (
        <Card key={group.letter} className="ra-section" aria-labelledby={`ra-path-${group.letter}`}>
          <h2 id={`ra-path-${group.letter}`}>{group.title}</h2>
          <ol className="ra-modules">
            {group.modules.map((module) => (
              <ModuleRow key={module.module} module={module} actions={actions} agentAvailable={agentAvailable} />
            ))}
          </ol>
        </Card>
      ))}
    </div>
  );
}
